import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { toast } from 'react-toastify';
import {
  ChartBarIcon,
  CurrencyEuroIcon,
  UsersIcon,
  BuildingOfficeIcon,
  PresentationChartLineIcon,
  TrophyIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  FunnelIcon,
  DocumentChartBarIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CalendarIcon,
  XMarkIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import API_CONFIG from '../config/api';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';

const ProvvigioniContrattiPage = () => {
  const [distribuzioni, setDistribuzioni] = useState([]);
  const [statistiche, setStatistiche] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFiltri, setLoadingFiltri] = useState(false);
  const [filtri, setFiltri] = useState({
    agenziaId: '',
    dataInizio: '',
    dataFine: '',
    tipoNodo: '',
    termineRicerca: ''
  });
  const [agenzieDisponibili, setAgenzieDisponibili] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  // Debounce per la ricerca
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  const ruoloUtente = (user?.role || user?.ruolo || '').toLowerCase();
  const isAdmin = ruoloUtente === 'admin';
  const isAgenzia = ruoloUtente === 'agenzia';
  const isSubagente = ruoloUtente === 'subagente' || ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale';

  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, canViewNode } = useNetworkHierarchy(user);

  // Funzione per determinare se mostrare la percentuale
  const shouldShowPercentage = (dist) => {
    // Admin vede tutto
    if (isAdmin) return true;
    
    // Non mostrare mai la propria percentuale
    if (dist.userId === user?.id) return false;
    
    // Mostra solo le percentuali dei nodi subordinati
    const isSubordinate = subordinateNodes.some(node => node.utenteId === dist.userId);
    return isSubordinate;
  };

  // Imposta i filtri di default in base al ruolo
  useEffect(() => {
    if (!isAdmin && user) {
      console.log('🔍 [DEBUG PROVVIGIONI] Impostazione filtri default per utente:', {
        userId: user.id,
        role: user.role,
        isAgenzia,
        isSubagente,
        userNode: userNode?.id,
        nodoReteId: user.nodoReteId,
        agenziaId: user.agenziaId,
        idAgenziaPrimaria: user.idAgenziaPrimaria
      });

      if (isAgenzia && userNode?.id) {
        // L'agenzia vede i contratti della sua agenzia
        setFiltri(prev => ({
          ...prev,
          agenziaId: userNode.id,
          tipoNodo: '',
        }));
        console.log('✅ [DEBUG PROVVIGIONI] Filtro agenzia impostato:', userNode.id);
      } else if (isSubagente) {
        // ✅ CORREZIONE: Gli agenti NON devono avere filtri troppo restrittivi
        // Lasciano che il backend applichi i filtri di sicurezza
        // e il frontend filtra solo in base alla presenza nelle distribuzioni
        console.log('✅ [DEBUG PROVVIGIONI] Agente - nessun filtro agenziaId applicato, il backend gestirà la sicurezza');
        setFiltri(prev => ({
          ...prev,
          agenziaId: '', // Non filtrare per agenzia specifica
          tipoNodo: '', // Non filtrare per tipo nodo
        }));
      }
    }
  }, [user, userNode, isAdmin, isAgenzia, isSubagente]);

  useEffect(() => {
    caricaDatiIniziali();
  }, []);

  // Effetto per debounce della ricerca
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (!loading) {
        caricaDistribuzioni();
        caricaStatistiche();
      }
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [filtri.termineRicerca]);

  // Effetto per altri filtri (immediato)
  useEffect(() => {
    if (!loading) {
      caricaDistribuzioni();
      caricaStatistiche();
    }
  }, [filtri.agenziaId, filtri.dataInizio, filtri.dataFine, filtri.tipoNodo]);

  const caricaDatiIniziali = async () => {
    try {
      setLoading(true);
      await Promise.all([
        caricaAgenzieDisponibili(),
        caricaDistribuzioni(),
        caricaStatistiche()
      ]);
    } catch (error) {
      console.error('Errore caricamento dati iniziali:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const caricaAgenzieDisponibili = async () => {
    try {
      console.log('🔍 Caricamento agenzie disponibili...');
      const response = await api.get(
        `${API_CONFIG.baseURL}/api/network/nodi?tipoNodo=agenzia_primaria`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('📋 Risposta API agenzie:', response.data);

      if (response.data.success) {
        const agenzie = response.data.data?.nodi || [];
        console.log(`✅ Trovate ${agenzie.length} agenzie`);
        setAgenzieDisponibili(agenzie);
        
        if (agenzie.length === 0) {
          console.log('⚠️ Nessuna agenzia trovata nel database');
        }
      } else {
        console.error('❌ Errore nella risposta API agenzie:', response.data);
        setAgenzieDisponibili([]);
      }
    } catch (error) {
      console.error('❌ Errore caricamento agenzie:', error);
      console.error('Dettagli errore:', error.response?.data);
      
      // In caso di errore, proviamo con un endpoint alternativo
      try {
        console.log('🔄 Tentativo con endpoint alternativo...');
        const alternativeResponse = await api.get(
          `${API_CONFIG.baseURL}/api/network/nodi`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              tipoNodo: 'agenzia_primaria',
              attivo: true
            }
          }
        );
        
        console.log('📋 Risposta endpoint alternativo:', alternativeResponse.data);
        
        if (alternativeResponse.data.success) {
          const agenzie = alternativeResponse.data.data?.nodi || [];
          setAgenzieDisponibili(agenzie);
          console.log(`✅ Agenzie caricate con endpoint alternativo: ${agenzie.length}`);
        }
      } catch (alternativeError) {
        console.error('❌ Errore anche con endpoint alternativo:', alternativeError);
        setAgenzieDisponibili([]);
      }
    }
  };

  const caricaDistribuzioni = useCallback(async () => {
    try {
      setLoadingFiltri(true);
      const queryParams = new URLSearchParams();
      
      if (filtri.agenziaId) queryParams.append('agenziaId', filtri.agenziaId);
      if (filtri.dataInizio) queryParams.append('dataInizio', filtri.dataInizio);
      if (filtri.dataFine) queryParams.append('dataFine', filtri.dataFine);
      if (filtri.tipoNodo) queryParams.append('tipoNodo', filtri.tipoNodo);

      console.log('🔍 [DEBUG PROVVIGIONI] Caricamento distribuzioni per utente:', {
        userId: user?.id,
        username: user?.username,
        role: user?.role,
        userNodeId: userNode?.id,
        subordinateNodes: subordinateNodes?.length,
        filtri
      });

      const response = await api.get(
        `${API_CONFIG.baseURL}/api/provvigioni-contratti/distribuzioni?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          timeout: 30000 // 30 secondi per query con filtri complessi
        }
      );

      console.log('🔍 [DEBUG FRONTEND] Risposta completa dal backend:', {
        status: response.status,
        success: response.data?.success,
        dataStructure: Object.keys(response.data || {}),
        distribuzioniCount: response.data?.data?.distribuzioni?.length || response.data?.distribuzioni?.length || 0,
        rawData: response.data
      });

      if (response.data.success) {
        let distribuzioniData = response.data.data?.distribuzioni || [];
        console.log('🔍 [DEBUG PROVVIGIONI] Distribuzioni ricevute dal backend:', distribuzioniData.length);
        
        // ✅ LOG DETTAGLIATO: Struttura della prima distribuzione se presente
        if (distribuzioniData.length > 0) {
          const primaDist = distribuzioniData[0];
          console.log('🔍 [DEBUG FRONTEND] Struttura prima distribuzione:', {
            contratto: primaDist.contratto,
            metadati: primaDist.metadati,
            distribuzioneArray: primaDist.distribuzione?.map(d => ({
              nodoId: d.nodoId,
              userId: d.userId,
              nomeUtente: d.nomeUtente,
              userIdType: typeof d.userId
            }))
          });
        }
        
        // Filtra i dati delle distribuzioni in base alla gerarchia dell'utente
        if (!isAdmin && userNode && subordinateNodes.length >= 0) {
          const nodiVisibili = [userNode.id, ...subordinateNodes.map(n => n.id)];
          const utentiVisibili = [user.id, ...subordinateNodes.map(n => n.utenteId).filter(Boolean)];
          
          console.log('🔍 [DEBUG PROVVIGIONI] Filtri applicati:', {
            nodiVisibili,
            utentiVisibili,
            userNodeId: userNode.id,
            isAdmin,
            isSubagente
          });
          
          distribuzioniData = distribuzioniData.filter(item => {
            // Controlla se l'utente può vedere questa distribuzione
            const agenziaId = item.metadati?.agenziaId;
            const isVisibleByNode = !agenziaId || nodiVisibili.includes(agenziaId);
            
            // ✅ MIGLIORAMENTO: Gli agenti vedono sempre le distribuzioni dove sono presenti
            const hasOwnDistribution = item.distribuzione?.some(dist => dist.userId === user.id);
            
            // Controlla anche se ci sono distribuzioni per utenti subordinati
            const hasVisibleDistributions = item.distribuzione?.some(dist => 
              shouldShowPercentage(dist) || dist.userId === user.id
            );
            
            // ✅ CORREZIONE: Un agente può vedere una distribuzione se:
            // 1. Appartiene alla sua agenzia/rete (isVisibleByNode)
            // 2. È presente nella distribuzione (hasOwnDistribution)
            // 3. Ha distribuzioni visibili (hasVisibleDistributions)
            const canViewDistribution = isVisibleByNode || hasOwnDistribution;
            const hasRelevantDistributions = hasVisibleDistributions || hasOwnDistribution || item.distribuzione?.length === 0;
            
            console.log('🔍 [DEBUG PROVVIGIONI] Valutazione distribuzione contratto:', item.contratto?.codiceProgressivo, {
              agenziaId,
              isVisibleByNode,
              hasOwnDistribution,
              hasVisibleDistributions,
              canViewDistribution,
              hasRelevantDistributions,
              finalResult: canViewDistribution && hasRelevantDistributions
            });
            
            return canViewDistribution && hasRelevantDistributions;
          });
        }
        
        console.log('🔍 [DEBUG PROVVIGIONI] Distribuzioni dopo filtri:', distribuzioniData.length);
        
        if (filtri.termineRicerca) {
          const termine = filtri.termineRicerca.toLowerCase();
          distribuzioniData = distribuzioniData.filter(item => 
            item.contratto?.codiceProgressivo?.toLowerCase().includes(termine) ||
            item.metadati?.clienteNome?.toLowerCase().includes(termine) ||
            item.metadati?.agenziaName?.toLowerCase().includes(termine)
          );
        }
        
        setDistribuzioni(distribuzioniData);
        console.log('🔍 [DEBUG PROVVIGIONI] Distribuzioni finali mostrate:', distribuzioniData.length);
      }
    } catch (error) {
      console.error('Errore caricamento distribuzioni:', error);
      toast.error('Errore nel caricamento delle distribuzioni');
    } finally {
      setLoadingFiltri(false);
    }
  }, [filtri]);

  const caricaStatistiche = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      
      if (filtri.agenziaId) queryParams.append('agenziaId', filtri.agenziaId);
      if (filtri.dataInizio) queryParams.append('dataInizio', filtri.dataInizio);
      if (filtri.dataFine) queryParams.append('dataFine', filtri.dataFine);

      const response = await api.get(
        `${API_CONFIG.baseURL}/api/provvigioni-contratti/statistiche?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          timeout: 30000
        }
      );

      if (response.data.success) {
        setStatistiche(response.data.data?.statistiche);
      }
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  }, [filtri.agenziaId, filtri.dataInizio, filtri.dataFine]);

  const handleFiltroChange = (campo, valore) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
  };

  const resetFiltri = () => {
    setFiltri({
      agenziaId: '',
      dataInizio: '',
      dataFine: '',
      tipoNodo: '',
      termineRicerca: ''
    });
  };

  const toggleExpanded = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getTipoNodoInfo = (tipo) => {
    switch (tipo) {
      case 'agenzia_primaria': 
        return { 
          label: 'Agenzia Primaria', 
          color: 'bg-blue-100 text-blue-800', 
          icon: <BuildingOfficeIcon className="h-4 w-4" />
        };
      case 'team_leader': 
        return { 
          label: 'Team Leader', 
          color: 'bg-purple-100 text-purple-800', 
          icon: <UsersIcon className="h-4 w-4" />
        };
      case 'agente': 
        return { 
          label: 'Agente', 
          color: 'bg-green-100 text-green-800', 
          icon: <UsersIcon className="h-4 w-4" />
        };
      default: 
        return { 
          label: tipo, 
          color: 'bg-gray-100 text-gray-800', 
          icon: <UsersIcon className="h-4 w-4" />
        };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const renderStatistiche = () => {
    if (!statistiche) return null;
    if (isSubagente || isAgenzia) return null; // Nascondi statistiche per subagente/team leader/agente/agenzia

    const stats = [
      {
        title: 'Totale Distribuito',
        value: formatCurrency(statistiche.totaleDistribuito),
        icon: <CurrencyEuroIcon className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-600" />,
        bgColor: 'bg-emerald-50',
        textColor: 'text-emerald-900'
      },
      {
        title: 'Contratti Attivi',
        value: statistiche.numeroContratti?.toLocaleString('it-IT') || '0',
        icon: <DocumentChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />,
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-900'
      },
      {
        title: 'Agenzie Coinvolte',
        value: statistiche.agenzieAttive?.toLocaleString('it-IT') || '0',
        icon: <BuildingOfficeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />,
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-900'
      },
      {
        title: 'Media per Contratto',
        value: formatCurrency(statistiche.mediaPerContratto),
        icon: <TrophyIcon className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />,
        bgColor: 'bg-amber-50',
        textColor: 'text-amber-900'
      }
    ];

    return (
      <div className="mb-6 sm:mb-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`${stat.bgColor} rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-100`}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">{stat.title}</p>
                  <p className={`text-lg sm:text-xl lg:text-2xl font-bold ${stat.textColor} truncate`}>{stat.value}</p>
                </div>
                <div className={`${stat.bgColor} p-2 sm:p-3 rounded-lg sm:rounded-xl ml-2 flex-shrink-0`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFiltri = () => (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
          <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Filtri e Ricerca</h3>
      </div>

      <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 mb-4 sm:mb-6">
        {/* Ricerca */}
        <div className="sm:col-span-2 lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ricerca per codice o cliente
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca contratto o cliente..."
              className="w-full pl-9 sm:pl-10 pr-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filtri.termineRicerca}
              onChange={(e) => handleFiltroChange('termineRicerca', e.target.value)}
            />
          </div>
        </div>

        {/* Agenzia: visibile solo per admin */}
        {!isAgenzia && !isSubagente && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Agenzia
          </label>
          <select
            className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={filtri.agenziaId}
            onChange={(e) => handleFiltroChange('agenziaId', e.target.value)}
            disabled={isAgenzia || isSubagente}
          >
            <option value="">
              {agenzieDisponibili.length === 0 
                ? 'Nessuna agenzia disponibile' 
                : 'Tutte le agenzie'
              }
            </option>
            {agenzieDisponibili.map((agenzia) => (
              <option key={agenzia.id} value={agenzia.id}>
                {agenzia.nome || `Agenzia ${agenzia.id}`} 
                {agenzia.utenteRete && ` (${agenzia.utenteRete.nome} ${agenzia.utenteRete.cognome})`}
              </option>
            ))}
          </select>
          {agenzieDisponibili.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              💡 Non sono presenti agenzie nel sistema
            </p>
          )}
        </div>
        )}

        {/* Tipo Nodo: visibile solo per admin */}
        {!isAgenzia && !isSubagente && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipo Utente
          </label>
          <select
            className="w-full px-3 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={filtri.tipoNodo}
            onChange={(e) => handleFiltroChange('tipoNodo', e.target.value)}
            disabled={isSubagente}
          >
            <option value="">Tutti i tipi</option>
            <option value="agenzia_primaria">Agenzia Primaria</option>
            <option value="team_leader">Team Leader</option>
            <option value="agente">Agente</option>
          </select>
        </div>
        )}

        {/* Data Inizio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data Inizio
          </label>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="date"
              className="w-full pl-9 sm:pl-10 pr-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filtri.dataInizio}
              onChange={(e) => handleFiltroChange('dataInizio', e.target.value)}
            />
          </div>
        </div>

        {/* Data Fine */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Data Fine
          </label>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            <input
              type="date"
              className="w-full pl-9 sm:pl-10 pr-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filtri.dataFine}
              onChange={(e) => handleFiltroChange('dataFine', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={resetFiltri}
          className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm font-medium"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Reset Filtri
        </button>
        
        {loadingFiltri && (
          <div className="inline-flex items-center justify-center px-4 py-2 text-gray-600 text-sm">
            <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
            Caricamento...
          </div>
        )}
      </div>
    </div>
  );

  const renderTabellaDistribuzioni = () => {
    // Filtro distribuzioni lato client per subagente: mostra solo le proprie
    let distribuzioniFiltrate = distribuzioni;
    if (isSubagente && user) {
      distribuzioniFiltrate = distribuzioni.filter(d => d.userId === user.id || d.userId === user.userId);
    } else if (isAgenzia && user) {
      // Filtro: mostra solo le distribuzioni dove l'utente loggato ha una quota nella distribuzione
      distribuzioniFiltrate = distribuzioni.filter(distribuzioneItem => {
        const userIdUtente = user.id;
        // L'agenzia deve vedere solo i contratti dove ha una quota
        return distribuzioneItem.distribuzione?.some(nodo => nodo.userId === userIdUtente);
      });
    }

    return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
            <PresentationChartLineIcon className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Distribuzione Provvigioni</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
                {distribuzioniFiltrate.length} {distribuzioniFiltrate.length === 1 ? 'contratto trovato' : 'contratti trovati'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
          {distribuzioniFiltrate.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <div className="p-3 bg-gray-100 rounded-full w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center">
              <DocumentChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Nessuna distribuzione trovata</h3>
            <p className="text-sm sm:text-base text-gray-600">
              Non ci sono distribuzioni che corrispondono ai filtri selezionati.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
              {distribuzioniFiltrate.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleExpanded(index)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <CreditCardIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                          {item.contratto?.codiceProgressivo || `Contratto ${item.contratto?.id}`}
                        </h4>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600 mt-1">
                          <span className="truncate">Cliente: {item.metadati?.clienteNome || 'N/D'}</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="truncate">Agenzia: {item.metadati?.agenziaName || 'N/D'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isAdmin || (isAgenzia && user && item.distribuzione?.some(d => d.userId === user.id && d.tipoNodo === 'agenzia_primaria')) && (
                      <div className="text-right">
                        <p className="text-base sm:text-lg font-bold text-emerald-600">
                          {formatCurrency(item.provvigioneTotale)}
                        </p>
                        <p className="text-xs text-gray-500 hidden sm:block">Totale distribuito</p>
                      </div>
                        )}
                      
                      <div className="text-gray-400">
                        {expandedItems[index] ? (
                          <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {expandedItems[index] && (
                  <div className="border-t border-gray-200 bg-white">
                    {item.errore ? (
                      <div className="p-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-800 font-medium text-sm">Errore nel calcolo</p>
                          <p className="text-red-600 text-xs sm:text-sm mt-1">{item.errore}</p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {/* Vista Mobile - Cards */}
                        <div className="block lg:hidden">
                          <div className="p-4 space-y-4">
                              {(isAgenzia && user
                                ? (item.distribuzione?.some(dist => dist.userId === user.id && dist.tipoNodo === 'agenzia_primaria')
                                    ? item.distribuzione
                                    : item.distribuzione?.filter(dist => dist.userId === user.id)
                                )
                                : isSubagente && user
                                  ? item.distribuzione?.filter(dist => dist.userId === user.id || dist.userId === user.userId)
                                  : item.distribuzione
                              )?.map((dist, idx) => {
                              const tipoInfo = getTipoNodoInfo(dist.tipoNodo);
                                const mostraBaseCalcolo = isAdmin || (isAgenzia && user && item.distribuzione?.some(d => d.userId === user.id && d.tipoNodo === 'agenzia_primaria'));
                              return (
                                <div key={idx} className="bg-gray-50 rounded-lg p-3 border">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                                        #{dist.ordineDistribuzione}
                                      </span>
                                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${tipoInfo.color}`}>
                                        {tipoInfo.icon}
                                        {tipoInfo.label}
                                      </span>
                                    </div>
                                    <span className="text-sm font-bold text-emerald-600">
                                      {formatCurrency(dist.importoProvvigione)}
                                    </span>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {dist.nomeUtente || 'N/D'}
                                      </p>
                                      <p className="text-xs text-gray-500">ID: {dist.userId}</p>
                                    </div>
                                    
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-gray-600">
                                        Percentuale: {shouldShowPercentage(dist) ? `${dist.percentualeApplicata}%` : 'N/D'}
                                      </span>
                                        {mostraBaseCalcolo && dist.tipoNodo === 'agenzia_primaria' && (
                                        <span className="text-gray-700 font-medium">Residuo agenzia: {formatCurrency((Number(dist.importoProvvigione) || 0) - (item.distribuzione || []).filter(d => d.tipoNodo !== 'agenzia_primaria').reduce((s, d) => s + (Number(d.importoProvvigione) || 0), 0))}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Vista Desktop - Tabella */}
                        <div className="hidden lg:block overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Ordine
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Utente
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Tipo
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Percentuale
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Importo
                                </th>
                                  {/* Definisco una variabile di utilità per la visibilità della base calcolo: */}
                                  {isAdmin || (isAgenzia && user && item.distribuzione?.some(dist => dist.userId === user.id && dist.tipoNodo === 'agenzia_primaria')) && (
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Residuo Agenzia
                                </th>
                                  )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {(isAgenzia && user
                                  ? (item.distribuzione?.some(dist => dist.userId === user.id && dist.tipoNodo === 'agenzia_primaria')
                                      ? item.distribuzione
                                      : item.distribuzione?.filter(dist => dist.userId === user.id)
                                  )
                                  : isSubagente && user
                                    ? item.distribuzione?.filter(dist => dist.userId === user.id || dist.userId === user.userId)
                                    : item.distribuzione
                                )?.map((dist, idx) => {
                                const tipoInfo = getTipoNodoInfo(dist.tipoNodo);
                                  const mostraBaseCalcolo = isAdmin || (isAgenzia && user && item.distribuzione?.some(d => d.userId === user.id && d.tipoNodo === 'agenzia_primaria'));
                                return (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                      #{dist.ordineDistribuzione}
                                    </td>
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-3">
                                        <div className="p-1.5 bg-gray-100 rounded-lg">
                                          {tipoInfo.icon}
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-gray-900">
                                            {dist.nomeUtente || 'N/D'}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            ID: {dist.userId}
                                          </p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${tipoInfo.color}`}>
                                        {tipoInfo.icon}
                                        {tipoInfo.label}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {shouldShowPercentage(dist) ? `${dist.percentualeApplicata}%` : 'N/D'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <span className="text-sm font-bold text-emerald-600">
                                        {formatCurrency(dist.importoProvvigione)}
                                      </span>
                                    </td>
                                      {mostraBaseCalcolo && (
                                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                                      {dist.tipoNodo === 'agenzia_primaria'
                                        ? formatCurrency((Number(dist.importoProvvigione) || 0) - (item.distribuzione || []).filter(d => d.tipoNodo !== 'agenzia_primaria').reduce((s, d) => s + (Number(d.importoProvvigione) || 0), 0))
                                        : '-'}
                                    </td>
                                      )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  };

  if (loading) {
    return (
      <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 text-sm sm:text-base">Caricamento dati in corso...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 sm:p-3 bg-blue-100 rounded-lg sm:rounded-xl flex-shrink-0">
            <ChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Provvigioni Contratti</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Visualizzazione delle provvigioni distribuite secondo la gerarchia configurata
            </p>
          </div>
        </div>
      </div>

      {renderStatistiche()}
      {renderFiltri()}
      {renderTabellaDistribuzioni()}
    </div>
  );
};

export default ProvvigioniContrattiPage; 